"use client";

import { Trophy, X } from "lucide-react";
import { useEffect, useState } from "react";
import Link from "@/components/ui/Link";
import { useUi } from "@/components/ui/use-ui";
import { ACHIEVEMENTS_EVENT, type Unlocked } from "@/lib/activity";
import { fmt } from "@/lib/locales/fmt";
import { SweetCoin } from "./SweetCoin";
import { refreshCoins } from "@/lib/coins-badge";

// Announces the achievements a member has just unlocked (lib/achievements.ts), as the activity reports and
// the daily bonus bring them (lib/activity.ts announceAchievements): one card at a time, each a few seconds,
// leading to /achievements.

const SHOW_MS = 5000;

export function AchievementToast() {
  const t = useUi();
  const [queue, setQueue] = useState<Unlocked[]>([]);

  useEffect(() => {
    const add = (e: Event) => {
      const unlocked = (e as CustomEvent<Unlocked[]>).detail;
      setQueue((q) => [...q, ...unlocked]);
      // Their coins are in the balance already: the header's coin follows.
      if (unlocked.some((u) => u.coins)) refreshCoins();
    };
    window.addEventListener(ACHIEVEMENTS_EVENT, add);
    return () => window.removeEventListener(ACHIEVEMENTS_EVENT, add);
  }, []);

  const current = queue[0];
  useEffect(() => {
    if (!current) return;
    const timer = window.setTimeout(() => setQueue((q) => q.slice(1)), SHOW_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  if (!current) return null;
  const [name, what] = t.achievements.names[current.key] ?? [current.key, ""];
  return (
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-24 z-[60] flex justify-center px-3 md:bottom-8">
      <div
        key={current.key}
        className="glass-strong pointer-events-auto flex w-full max-w-sm animate-toast-in items-center gap-3 rounded-2xl py-3 pl-3 pr-2 shadow-[0_24px_70px_-20px_rgb(246_196_83/0.55)]"
      >
        <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[#f6c453]/15 text-[#f6c453]">
          <Trophy size={22} />
        </span>
        <Link href="/achievements" onClick={() => setQueue((q) => q.slice(1))} className="min-w-0 flex-1">
          <span className="block text-[11.5px] uppercase tracking-[0.08em] text-[#f6c453]">{t.achievements.toast}</span>
          <span className="block truncate text-[15px] text-fg">{name}</span>
          {what && <span className="block truncate text-[12.5px] text-muted">{what}</span>}
        </Link>
        <span className="flex shrink-0 items-center gap-1.5 text-[13.5px] tabular-nums text-[#f6c453]">
          <SweetCoin size={18} />
          {fmt(t.achievements.coins, { n: current.coins })}
        </span>
        <button
          type="button"
          onClick={() => setQueue((q) => q.slice(1))}
          aria-label={t.shorts.close}
          className="grid size-8 shrink-0 place-items-center rounded-full text-faint hover:bg-white/10 hover:text-fg"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
